import React, { useEffect, useState } from "react";
import Select from "react-select";
import Popup from "reactjs-popup";
import Loader from "../../Snippets/Loader";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { FaSpinner } from "react-icons/fa";
import { getCMSRoleById } from "../../../Services/CMSUserRoles/UserRolesData";

const tabs = [
  "cms_roles",
  "roles",
  "arbitration",
  "order",
  "efilling",
  "user",
  "organization",
  "blogs",
  "commence",
  "payment",
];

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const CMSUserAction = ({
  isPreviewOpen,
  userId,
  roles,
  operation,
  handleClose,
  handleSubmit,
  handleUpdate,
  formData,
  isLoadingUpdate,
}) => {

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateUserValue, setUpdatedUserValues] = useState({});
  const [allChecked, setAllChecked] = useState(false);

  useEffect(() => {
    if (operation === "update") {
      const getUserById = async () => {
        setIsLoading(true);
        try {
          const data = await getCMSRoleById(userId);

          let permission;
          if (data?.permissions.includes("*")) {
            permission = ["all"];
            setAllChecked(true);
          } else {
            permission = data?.permissions;
          }

          setUpdatedUserValues({
            email: data?.email,
            name: data?.name,
            phone_number: data?.phone_number,
            gender: data?.gender,
            role: data?.role,
            permission,
          });

          setIsLoading(false);
        } catch {
          setIsLoading(false);
        }
      };

      getUserById();
      setIsEditing(true);
    }
  }, [operation, userId]);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is Required"),
    email: Yup.string().required("Email is Required"),
    phone_number: Yup.string().required("Mobile Number is Required"),
    gender: Yup.string().required("Gender is Required"),
    role: Yup.string().required("Role is Required"),
  });

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    let updatedPermissions = [...(updateUserValue.permission || [])];

    if (value === "all") {
      setAllChecked(checked);
      updatedPermissions = checked ? ["*"] : [];
    } else {
      if (checked) updatedPermissions.push(value);
      else updatedPermissions = updatedPermissions.filter((p) => p !== value);
      setAllChecked(false);
    }

    setUpdatedUserValues((prev) => ({
      ...prev,
      permission: updatedPermissions,
    }));
  };

  return (
    <Popup open={isPreviewOpen} modal closeOnDocumentClick={false}>
      {(close) => (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-3 z-50">

          {isLoading ? (
            <Loader />
          ) : (

            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">

              <div className="p-6">

                {/* Close */}
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      handleClose();
                      close();
                    }}
                    className="text-xl"
                  >
                    ✕
                  </button>
                </div>

                <h2 className="text-lg font-semibold mb-6">
                  {isEditing ? "Update CMS User" : "Add CMS User"}
                </h2>

                <Formik
                  initialValues={isEditing ? updateUserValue : formData}
                  validationSchema={validationSchema}
                  enableReinitialize
                  onSubmit={(values) =>
                    isEditing
                      ? handleUpdate(values, updateUserValue.permission)
                      : handleSubmit(values, updateUserValue.permission)
                  }
                >
                  {(formikProps) => {

                    const roleOptions = roles?.map((r) => ({
                      value: r.name,
                      label: r.name,
                    }));

                    return (

                      <Form className="space-y-5">

                        {/* Name + Phone */}
                        <div className="grid md:grid-cols-2 gap-4">

                          <div>
                            <label className="label-style">Name *</label>
                            <Field
                              name="name"
                              className="input-style w-full"
                              placeholder="Enter Name"
                            />
                            <ErrorMessage name="name" component="div" className="error"/>
                          </div>

                          <div>
                            <label className="label-style">Phone *</label>
                            <Field
                              name="phone_number"
                              className="input-style w-full"
                              placeholder="Enter phone"
                            />
                            <ErrorMessage name="phone_number" component="div" className="error"/>
                          </div>

                        </div>

                        {/* Email */}
                        <div>
                          <label className="label-style">Email *</label>
                          <Field
                            name="email"
                            type="email"
                            className="input-style w-full"
                            placeholder="Enter email"
                          />
                          <ErrorMessage name="email" component="div" className="error"/>
                        </div>

                        {/* Gender + Role */}
                        <div className="grid md:grid-cols-2 gap-4">

                          {/* Gender */}
                          <div>
                            <label className="label-style">Gender *</label>

                            <Select
                              options={genderOptions}
                              placeholder="Select Gender"
                              isSearchable={false}
                              value={genderOptions.find(
                                (option) => option.value === formikProps.values.gender
                              )}
                              onChange={(option) =>
                                formikProps.setFieldValue("gender", option.value)
                              }
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />

                            <ErrorMessage name="gender" component="div" className="error"/>
                          </div>

                          {/* Role */}
                          <div>
                            <label className="label-style">Role *</label>

                            <Select
                              options={roleOptions}
                              placeholder="Select Role"
                              value={roleOptions?.find(
                                (option) => option.value === formikProps.values.role
                              )}
                              onChange={(option) =>
                                formikProps.setFieldValue("role", option.value)
                              }
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />

                            <ErrorMessage name="role" component="div" className="error"/>
                          </div>

                        </div>

                        {/* Permissions */}
                        <div>

                          <label className="label-style mb-2 block">
                            Tab Access *
                          </label>

                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">

                            <label className="flex gap-2 items-center">
                              <input
                                type="checkbox"
                                value="all"
                                checked={allChecked}
                                onChange={handleCheckboxChange}
                              />
                              All
                            </label>

                            {tabs.map((permission) => (
                              <label key={permission} className="flex gap-2 items-center">

                                <input
                                  type="checkbox"
                                  value={permission}
                                  checked={
                                    updateUserValue.permission?.includes(permission) || false
                                  }
                                  onChange={handleCheckboxChange}
                                  disabled={allChecked}
                                />

                                {permission}

                              </label>
                            ))}

                          </div>

                        </div>

                        {/* Submit */}
                        <div className="flex justify-center pt-4">

                          <button
                            type="submit"
                            className="bg-blue-600 text-white px-8 py-2 rounded-lg hover:bg-blue-700"
                          >
                            {isLoadingUpdate ? (
                              <FaSpinner className="animate-spin" />
                            ) : (
                              "Submit"
                            )}
                          </button>

                        </div>

                      </Form>

                    );
                  }}
                </Formik>

              </div>
            </div>
          )}
        </div>
      )}
    </Popup>
  );
};

export default CMSUserAction;